import React, { useState } from "react";
import {
  FooterBackground,
  FooterColumns,
  FooterColumn,
  FooterRowLinks,
  FooterRowBottomLeftLink,
  FooterRowBottomRightLink,
  FooterRowBottom,
  StyledLink,
  StyledLinkMobile,
  XometryLogoMark,
} from "./footer.styled";
import GreenBadge from "../green-badge/green-badge";
import { FooterQuery, PrismicRichTextField, Maybe } from "../../types/graphql";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { PrismicRichText as PrismicRichTextReact } from "@prismicio/react";
import * as FA from "../../styles/fontawesome";
import { SerializedStyles, css } from "@emotion/react";
import VisibilitySensor from "react-visibility-sensor";

interface IFooterProps {
  data: FooterQuery;
}

function FooterSocial(props: {
  href: string;
  iconId: string;
  icon: SerializedStyles[] | "";
  ariaLabel: string;
}) {
  return (
    <a
      id={props.iconId}
      href={props.href}
      target="_blank"
      rel="nofollow noopener noreferrer"
      aria-label={"Follow Xometry on " + props.ariaLabel}
    >
      {props.icon && <i css={[FA.DoubleSize, ...props.icon]}></i>}
    </a>
  );
}

export const PureFooter = (props: IFooterProps) => {
  const footer = props.data.prismicFooter?.data;
  const [hasViewed, setHasViewed] = useState(false);

  function renderColumnLinks(
    title: string | null | undefined,
    items:
      | Maybe<
          Maybe<{
            column_item?:
              | Maybe<Pick<PrismicRichTextField, "richText">>
              | undefined;
          }>[]
        >
      | undefined
  ) {
    return (
      <FooterColumn>
        {title && <h6>{title}</h6>}
        {items && items.length > 0 && (
          <ul>
            {items.map((link, index) => {
              return (
                link?.column_item && (
                  <li key={index}>
                    <PrismicRichTextReact
                      field={link.column_item.richText}
                      components={htmlSerializer}
                    />
                  </li>
                )
              );
            })}
          </ul>
        )}
      </FooterColumn>
    );
  }

  return (
    (footer && (
      <FooterBackground>
        <VisibilitySensor
          onChange={(isVisible: boolean) => isVisible && setHasViewed(true)}
        >
          <div className="container">
            <FooterColumns className="grid">
              <div className="col-span-4 lg-col-span-6 two-column-list">
                {renderColumnLinks(footer.column_title_1, footer.column_text_1)}
                <StyledLink
                  to={"/xometry-go-green/"}
                  style={{ textDecoration: "none" }}
                >
                  {props.data.emissions?.total_lbs && hasViewed && (
                    <GreenBadge lbsOffset={props.data.emissions.total_lbs} />
                  )}
                </StyledLink>
              </div>
              <div className="col-span-4 lg-col-span-3">
                {renderColumnLinks(footer.column_title_2, footer.column_text_2)}
              </div>
              <div className="col-span-4 lg-col-span-3">
                {renderColumnLinks(footer.column_title_3, footer.column_text_3)}
              </div>
            </FooterColumns>
            <StyledLinkMobile
              to={"/xometry-go-green/"}
              style={{ textDecoration: "none" }}
            >
              {props.data.emissions?.total_lbs && hasViewed && (
                <GreenBadge lbsOffset={props.data.emissions.total_lbs} />
              )}
            </StyledLinkMobile>
            {footer.legal_links && footer.legal_links.length > 0 && (
              <FooterRowLinks>
                <PrismicRichTextReact
                  field={footer.legal_links[1].legal_link.richText}
                  components={htmlSerializer}
                />
              </FooterRowLinks>
            )}
            <hr />
            <div className="grid">
              <div className="col-span-4 md-col-span-8 lg-col-span-7">
                <FooterRowBottomLeftLink>
                  {hasViewed && <XometryLogoMark />}
                  <span>
                    &copy; {new Date().getFullYear()} Xometry, All Rights
                    Reserved.
                  </span>
                  {footer.legal_links && footer.legal_links.length > 0 && (
                    <PrismicRichTextReact
                      field={footer.legal_links[0].legal_link.richText}
                      components={htmlSerializer}
                    />
                  )}
                </FooterRowBottomLeftLink>
              </div>
              <div className="col-span-4 md-col-span-8 lg-col-span-5">
                <FooterRowBottomRightLink>
                  <FooterSocial
                    href="https://www.facebook.com/xometry"
                    iconId="facebook"
                    icon={hasViewed ? FA.Facebook : ""}
                    ariaLabel="Facebook"
                  />
                  <FooterSocial
                    href="https://www.x.com/xometry"
                    iconId="twitter"
                    icon={hasViewed ? FA.Twitter : ""}
                    ariaLabel="Twitter"
                  />
                  <FooterSocial
                    href="https://www.instagram.com/xometry"
                    iconId="instagram"
                    icon={hasViewed ? FA.Instagram : ""}
                    ariaLabel="Instagram"
                  />
                  <FooterSocial
                    href="https://www.youtube.com/channel/UCF3PKELz1eWO3bdxVLIYN7g"
                    iconId="youtube"
                    icon={hasViewed ? FA.Youtube : ""}
                    ariaLabel="YouTube"
                  />
                  <FooterSocial
                    href="https://www.linkedin.com/company/xometry"
                    iconId="linkedin-in"
                    icon={hasViewed ? FA.Linkedin_In : ""}
                    ariaLabel="LinkedIn"
                  />
                </FooterRowBottomRightLink>
              </div>
            </div>
          </div>
        </VisibilitySensor>
      </FooterBackground>
    )) || <></>
  );
};
